import Table, { ColumnsType } from 'antd/lib/table';
import { Link } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Space, message, Skeleton, Spin, Popover, Popconfirm } from 'antd';
import { useQuery } from '@tanstack/react-query';

import useTranslation from 'customHooks/translate';
import {
  getGetIncomesAndExpensesList,
  cancelInvoice,
  cancelCost,
} from 'services/invoices';
import { CreateInvoiceDataType } from 'types';
import { INCOMES_AND_EXPENCES } from 'events/types';
import { EditDeleteIcon, LoadingIndicator, TableWrapper } from 'styled';

const CreateInvoice = () => {
  const translation = useTranslation();

  const [pageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [invoicesData, setInvoicesData] = useState<CreateInvoiceDataType[]>([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Refs for scroll container and loading states
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);

  // Function to fetch invoices data
  const fetchInvoicesData = useCallback(
    async (currentPage: number, isNewRequest = false) => {
      if (isFetchingRef.current) return null;

      isFetchingRef.current = true;
      setIsLoadingMore(true);

      try {
        const response = await getGetIncomesAndExpensesList(
          pageSize,
          currentPage
        );
        const data = response.data;

        // Ensure we have data to work with
        if (data?.data?.length) {
          const formattedData = data.data.map((item: any) => ({
            ...item,
            key: item.id,
          }));

          // Set data based on whether this is a new request or not
          if (isNewRequest) {
            setInvoicesData(formattedData);
          } else {
            setInvoicesData((prev) => [...prev, ...formattedData]);
          }

          // Check if we have more data to load
          const newTotalItems =
            (isNewRequest ? 0 : invoicesData.length) + formattedData.length;
          setHasMoreData(newTotalItems < data.totalRecord);

          return { data: formattedData, totalRecord: data.totalRecord };
        }

        setHasMoreData(false);
        return null;
      } catch {
        return null;
      } finally {
        setIsLoadingMore(false);
        isFetchingRef.current = false;
      }
    },
    [pageSize, invoicesData.length]
  );

  // Initial data fetch with React Query
  const { status, isLoading } = useQuery({
    queryKey: [INCOMES_AND_EXPENCES, pageSize, pageIndex],
    queryFn: () => fetchInvoicesData(pageIndex, pageIndex === 0),
    keepPreviousData: true,
    refetchOnMount: 'always',
  });

  // Reset data function
  const resetData = () => {
    setInvoicesData([]);
    setPageIndex(0);
    setHasMoreData(true);
  };

  // Refetch data
  const refetch = () => {
    resetData();
    fetchInvoicesData(0, true);
  };

  const handleDelete = async (record) => {
    try {
      let response;

      if (record.invoiceType === 'Invoice') {
        response = await cancelInvoice(record.invoiceId);
      } else {
        response = await cancelCost(record.invoiceId);
      }

      if (response?.status === 200) {
        message.success(`${translation.messages.successDeleted}`);
        refetch();
      } else {
        throw new Error('Failed to delete item');
      }
    } catch (error) {
      message.error('An error occurred while deleting the item');
    }
  };

  // Handle table scroll
  const handleTableScroll = useCallback(() => {
    if (
      !tableContainerRef.current ||
      !loadingRef.current ||
      isFetchingRef.current ||
      !hasMoreData
    )
      return;

    const containerRect = tableContainerRef.current.getBoundingClientRect();
    const loadingRect = loadingRef.current.getBoundingClientRect();

    // Check if the loading element is visible
    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = pageIndex + 1;
      setPageIndex(nextPage);
      fetchInvoicesData(nextPage);
    }
  }, [pageIndex, hasMoreData, fetchInvoicesData]);

  // Set up scroll event listener
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener('scroll', handleTableScroll);
      return () => {
        tableContainer.removeEventListener('scroll', handleTableScroll);
      };
    }
  }, [handleTableScroll]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const columns: ColumnsType<CreateInvoiceDataType> = [
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.create.Table_Name}
        </span>
      ),
      dataIndex: 'partnerName',
      key: 'partnerName',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.create.Table_JobNumb}
        </span>
      ),
      dataIndex: 'jobNumber',
      key: 'jobNumber',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.create.Table_BankAcct}
        </span>
      ),
      dataIndex: 'bankName',
      key: 'bankName',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.create.Table_Manager}
        </span>
      ),
      dataIndex: 'manager',
      key: 'manager',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.create.Table_Currency}
        </span>
      ),
      dataIndex: 'currency',
      key: 'currency',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.create.Table_ExcRate}
        </span>
      ),
      dataIndex: 'exchangeRate',
      key: 'exchangeRate',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.create.InvoiceType}
        </span>
      ),
      dataIndex: 'invoiceType',
      key: 'invoiceType',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.create.Table_ContNum}
        </span>
      ),
      dataIndex: 'contractNumber',
      key: 'contractNumber',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.action.Action}
        </span>
      ),
      dataIndex: 'actions',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) =>
        isMobile ? (
          <Popover
            placement="bottom"
            trigger="click"
            content={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Link
                  to={
                    record.invoiceType === 'Invoice'
                      ? `/invoices/${record.invoiceId}/detail`
                      : `/cost/${record.invoiceId}/detail`
                  }
                  state={{ record }}
                >
                  <EditDeleteIcon src="assets/edit-icon.svg" alt="Edit" />
                </Link>

                <Popconfirm
                  title={translation.DeleteItem}
                  onConfirm={() => handleDelete(record)}
                  okText="Yes"
                  placement="left"
                  cancelText="No"
                >
                  <EditDeleteIcon src="assets/delete-icon.svg" alt="Delete" />
                </Popconfirm>
              </div>
            }
          >
            <MoreVertIcon
              style={{
                fontSize: '29px',
                border: '1px solid #292d32bf',
                borderRadius: '50%',
                padding: '4px',
              }}
            />
          </Popover>
        ) : (
          <Space size="middle">
            <Link
              to={
                record.invoiceType === 'Invoice'
                  ? `/invoices/${record.invoiceId}/detail`
                  : `/cost/${record.invoiceId}/detail`
              }
              state={{ record }}
            >
              <div style={{ cursor: 'pointer' }}>
                <img className="edit" src="assets/edit-icon.svg" alt="" />
              </div>
            </Link>
            <Popconfirm
              title={translation.DeleteItem}
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <div style={{ cursor: 'pointer' }}>
                <img
                  className="delete"
                  src="assets/delete-icon.svg"
                  alt="Delete"
                />
              </div>
            </Popconfirm>
          </Space>
        ),
    },
  ];

  return (
    <>
      {status === 'loading' && invoicesData.length === 0 ? (
        <Skeleton active />
      ) : (
        <TableWrapper ref={tableContainerRef}>
          <Table
            sticky={{
              offsetHeader: 0,
            }}
            scroll={{ x: 'max-content' }}
            dataSource={invoicesData}
            columns={columns}
            loading={isLoading && invoicesData.length === 0}
            pagination={false}
          />

          <LoadingIndicator ref={loadingRef}>
            {isLoadingMore && <Spin size="default" />}
            {!hasMoreData && invoicesData.length > 0 && (
              <div style={{ color: '#999', marginTop: '8px' }}>
                {translation.messages?.allData || 'All data loaded'}
              </div>
            )}
          </LoadingIndicator>
        </TableWrapper>
      )}
    </>
  );
};

export default CreateInvoice;
